import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { format } from 'date-fns';
import { ArrowLeft, Building2, Mail, AlertCircle, User, UserPlus, Trash2, ExternalLink, Linkedin, Phone, Copy, Check, Edit2, CheckSquare, Phone as PhoneIcon, Linkedin as LinkedinIcon, ListTodo, Calendar, X, Plus, Repeat, ChevronRight, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import ConversationView from '../components/ConversationView';
import TimelineView from '../components/TimelineView';
import ActivityCalendar from '../components/ActivityCalendar';
import { useAuth } from '../context/AuthContext';
import TagSelect from '../components/TagSelect';
import { Badge } from '../components/ui/badge';
import ScheduleFollowUpModal from '../components/ScheduleFollowUpModal';
import LogInteractionModal from '../components/LogInteractionModal';
import { getApiError } from '../utils/apiError';
import FileUpload from '../components/FileUpload';
import FileGallery from '../components/FileGallery';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LeadDetailPage = () => {
    const { leadId } = useParams();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user, isAdmin } = useAuth();
    const [lead, setLead] = useState(null);
    const [teamMembers, setTeamMembers] = useState([]);
    const [collisionWarning, setCollisionWarning] = useState(null);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingLead, setEditingLead] = useState(null);
    const [copySuccess, setCopySuccess] = useState(false);
    const [tasks, setTasks] = useState([]);
    const [taskForm, setTaskForm] = useState({
        description: '',
        type: 'Todo',
        due_date: new Date().toISOString().split('T')[0],
        assignee_id: user?.id || ''
    });
    const [creatingTask, setCreatingTask] = useState(false);
    const [logInteractionOpen, setLogInteractionOpen] = useState(false);
    const [completingTask, setCompletingTask] = useState(null);
    const [tags, setTags] = useState([]);
    const [editingTags, setEditingTags] = useState(false);
    const [savingTags, setSavingTags] = useState(false);
    const [stages, setStages] = useState([]);
    const [cadenceEnrollments, setCadenceEnrollments] = useState([]);
    const [followUpModalOpen, setFollowUpModalOpen] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [filesRefreshKey, setFilesRefreshKey] = useState(0);

    // Get default tab from URL query parameter (for deep linking from Google Chat)
    const viewParam = searchParams.get('view');
    const validViews = ['about', 'timeline', 'conversation', 'tasks', 'activity', 'files'];
    const defaultTab = validViews.includes(viewParam) ? viewParam : 'about';

    const fetchLead = async () => {
        try {
            setLoading(true);
            await Promise.all([
                axios.get(`${API_URL}/leads/${leadId}`).then(r => setLead(r.data)),
                fetchStages()
            ]);

            // Mark as viewing
            try {
                await axios.post(`${API_URL}/leads/${leadId}/view`);
            } catch (error) {
                console.error('Failed to mark viewing:', error);
            }
        } catch (error) {
            toast.error('Failed to load lead');
            navigate('/pipeline');
        } finally {
            setLoading(false);
        }
    };

    const fetchTeamMembers = async () => {
        try {
            const response = await axios.get(`${API_URL}/team/members`);
            setTeamMembers(response.data);
        } catch (error) {
            console.error('Failed to fetch team members:', error);
        }
    };

    const fetchStages = async () => {
        try {
            const response = await axios.get(`${API_URL}/pipeline/stages`);
            setStages(response.data);
        } catch (error) {
            console.error('Failed to fetch stages:', error);
        }
    };

    const fetchTasks = async () => {
        if (!leadId) return;
        try {
            const [tasksRes, enrollRes] = await Promise.all([
                axios.get(`${API_URL}/tasks`, { params: { lead_id: leadId, status: 'Pending' } }),
                axios.get(`${API_URL}/leads/${leadId}/cadence-enrollments`),
            ]);
            setTasks(tasksRes.data);
            setCadenceEnrollments(enrollRes.data);
        } catch (error) {
            console.error('Failed to fetch tasks:', error);
        }
    };

    const handleCreateTask = async (e) => {
        e.preventDefault();
        if (!taskForm.description.trim() || !taskForm.due_date) {
            toast.error('Description and Due Date are required');
            return;
        }

        try {
            setCreatingTask(true);
            // Parse "YYYY-MM-DD" string into components and create local end-of-day date
            const [year, month, day] = taskForm.due_date.split('-').map(Number);
            const dueDate = new Date(year, month - 1, day, 23, 59, 59, 999); // month is 0-indexed

            await axios.post(`${API_URL}/tasks`, {
                lead_id: leadId,
                description: taskForm.description.trim(),
                type: taskForm.type,
                due_date: dueDate.toISOString(),
                assignee_id: taskForm.assignee_id
            });
            toast.success('Task created successfully');
            setTaskForm({
                description: '',
                type: 'Todo',
                due_date: new Date().toISOString().split('T')[0],
                assignee_id: user?.id || ''
            });
            fetchTasks();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to create task'));
        } finally {
            setCreatingTask(false);
        }
    };

    const handleCancelEnrollment = async (enrollmentId) => {
        if (!window.confirm('Cancel this cadence? All pending tasks from it will be deleted.')) return;
        try {
            await axios.delete(`${API_URL}/cadence-enrollments/${enrollmentId}`);
            toast.success('Cadence cancelled');
            fetchTasks();
        } catch (error) {
            toast.error('Failed to cancel cadence');
        }
    };

    const handleDeleteTask = async (taskId) => {
        try {
            await axios.delete(`${API_URL}/tasks/${taskId}`);
            toast.success('Task deleted successfully');
            fetchTasks();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to delete task'));
        }
    };

    const handleCompleteTask = (taskId) => {
        setCompletingTask(taskId);
        setLogInteractionOpen(true);
    };

    const handleInteractionSaved = async () => {
        try {
            await axios.patch(`${API_URL}/tasks/${completingTask}`, {
                status: 'Completed'
            });
            toast.success('Task marked as complete');
            fetchTasks();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to complete task'));
        } finally {
            setLogInteractionOpen(false);
            setCompletingTask(null);
        }
    };

    const handleLogInteractionClose = () => {
        setLogInteractionOpen(false);
        setCompletingTask(null);
    };

    const getTaskTypeIcon = (type) => {
        switch (type) {
            case 'Call':
                return <PhoneIcon className="h-4 w-4" />;
            case 'Email':
                return <Mail className="h-4 w-4" />;
            case 'LinkedIn':
                return <LinkedinIcon className="h-4 w-4" />;
            case 'Todo':
                return <ListTodo className="h-4 w-4" />;
            default:
                return <CheckSquare className="h-4 w-4" />;
        }
    };

    const checkCollisions = useCallback(async () => {
        if (!leadId) return;

        try {
            const response = await axios.get(`${API_URL}/leads/${leadId}`);
            const leadData = response.data;

            if (leadData.viewing_now && leadData.viewing_now.user_email) {
                const viewingTime = new Date(leadData.viewing_now.timestamp);
                const now = new Date();
                const diffSeconds = (now - viewingTime) / 1000;

                if (diffSeconds < 60 && leadData.viewing_now.user_email !== user?.email) {
                    setCollisionWarning(leadData.viewing_now.user_email);
                } else {
                    setCollisionWarning(null);
                }
            }
        } catch (error) {
            console.error('Collision check failed:', error);
        }
    }, [leadId, user?.email]);

    useEffect(() => {
        if (leadId) {
            fetchLead();
            fetchTeamMembers();
            fetchTasks();
            fetchTags();
            const interval = setInterval(checkCollisions, 3000);
            return () => clearInterval(interval);
        }
    }, [leadId, checkCollisions]);

    const fetchTags = async () => {
        try {
            const response = await axios.get(`${API_URL}/tags`);
            setTags(response.data);
        } catch (error) {
            console.error('Failed to fetch tags:', error);
        }
    };

    const handleTagsChange = async (newTagIds) => {
        try {
            setSavingTags(true);
            // First refresh tags list in case new tags were created
            await fetchTags();
            // Then update the lead
            await axios.patch(`${API_URL}/leads/${leadId}`, { tags: newTagIds });
            // Update lead state with new tags
            setLead(prev => ({ ...prev, tags: newTagIds }));
            setEditingTags(false);
            toast.success('Tags updated');
        } catch (error) {
            toast.error('Failed to update tags');
            console.error('Failed to update tags:', error);
        } finally {
            setSavingTags(false);
        }
    };

    useEffect(() => {
        if (user?.id && !taskForm.assignee_id) {
            setTaskForm(prev => ({ ...prev, assignee_id: user.id }));
        }
    }, [user]);

    const assignLead = async (ownerId) => {
        try {
            await axios.post(`${API_URL}/leads/${leadId}/assign`, { owner_id: ownerId });
            toast.success('Lead assigned successfully');
            fetchLead();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to assign lead'));
        }
    };

    const claimLead = async () => {
        try {
            await axios.post(`${API_URL}/leads/${leadId}/claim`);
            toast.success('Lead claimed successfully');
            fetchLead();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to claim lead'));
        }
    };

    const handleDeleteLead = async () => {
        try {
            setDeleting(true);
            await axios.delete(`${API_URL}/leads/${leadId}`);
            toast.success('Lead deleted successfully');
            navigate('/pipeline');
        } catch (error) {
            toast.error(getApiError(error, 'Failed to delete lead'));
            setDeleting(false);
        }
    };

    const handleEditClick = () => {
        setEditingLead({
            name: lead.name,
            email: lead.email,
            company: lead.company || '',
            source: lead.source || 'Cold Outbound',
            job_title: lead.job_title || '',
            website: lead.website || '',
            phone_number: lead.phone_number || '',
            linkedin_profile: lead.linkedin_profile || '',
            estimated_value: lead.estimated_value || '',
            initial_notes: lead.initial_notes || '',
            do_not_contact: lead.do_not_contact || false,
        });
        setEditDialogOpen(true);
    };

    const handleUpdateLead = async () => {
        try {
            await axios.patch(`${API_URL}/leads/${leadId}`, editingLead);
            toast.success('Lead updated successfully');
            setEditDialogOpen(false);
            fetchLead();
        } catch (error) {
            toast.error(getApiError(error, 'Failed to update lead'));
        }
    };

    const copyToClipboard = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
            toast.success('Copied to clipboard');
        } catch (error) {
            toast.error('Failed to copy');
        }
    };

    const getOwnerInfo = (ownerId) => {
        if (!ownerId) return null;
        return teamMembers.find(m => m.id === ownerId);
    };

    const getInitials = (email) => {
        if (!email) return '?';
        const username = email.split('@')[0];

        if (username.includes('.')) {
            const parts = username.split('.').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } else if (parts.length === 1 && parts[0].length >= 2) {
                return parts[0].substring(0, 2).toUpperCase();
            }
        } else if (username.includes('_')) {
            const parts = username.split('_').filter(p => p.length > 0);
            if (parts.length >= 2) {
                return (parts[0][0] + parts[1][0]).toUpperCase();
            } else if (parts.length === 1 && parts[0].length >= 2) {
                return parts[0].substring(0, 2).toUpperCase();
            }
        }

        return username.length >= 2 ? username.substring(0, 2).toUpperCase() : username.toUpperCase() + '?';
    };

    const getSentimentColor = (sentiment) => {
        switch (sentiment) {
            case 'Positive':
                return 'text-green-600 bg-green-50';
            case 'Negative':
                return 'text-red-600 bg-red-50';
            case 'Neutral':
                return 'text-slate-600 bg-slate-50';
            default:
                return 'text-slate-600 bg-slate-50';
        }
    };

    if (loading) {
        return (
            <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900 mx-auto mb-4"></div>
                    <p className="text-slate-600">Loading lead...</p>
                </div>
            </div>
        );
    }

    if (!lead) {
        return (
            <div className="h-[calc(100vh-4rem)] flex items-center justify-center">
                <div className="text-center">
                    <p className="text-slate-600 mb-4">Lead not found</p>
                    <Button onClick={() => navigate('/pipeline')}>Back to Pipeline</Button>
                </div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-4rem)] bg-slate-50/50 flex flex-col">
            {/* Header */}
            <div className="bg-white border-b border-slate-200 p-6">
                <div className="flex items-center gap-4 mb-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate('/pipeline')}
                        className="rounded-lg"
                    >
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Pipeline
                    </Button>
                </div>

                <div className="flex items-start justify-between">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900 mb-2">{lead.name}</h1>
                        <div className="flex items-center gap-4 text-sm text-slate-600 flex-wrap">
                            {lead.do_not_contact && (
                                <Badge variant="destructive" className="rounded-md">Do Not Contact</Badge>
                            )}
                            <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4" />
                                <span>{lead.email}</span>
                            </div>
                            {lead.company && (
                                <div className="flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    <span>{lead.company}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <Button
                            variant="outline"
                            size="sm"
                            className="rounded-lg"
                            onClick={() => setFollowUpModalOpen(true)}
                        >
                            <Calendar className="h-4 w-4 mr-2" />
                            Schedule Follow-up
                        </Button>

                        {collisionWarning && (
                            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2">
                                <AlertCircle className="h-5 w-5 text-amber-600" />
                                <p className="text-sm text-amber-800">
                                    <strong>Warning:</strong> {collisionWarning} is currently viewing this lead.
                                </p>
                            </div>
                        )}

                        {isAdmin() && (
                            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        variant="destructive"
                                        size="sm"
                                        className="rounded-lg"
                                    >
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Delete Lead
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            This action cannot be undone. This will permanently delete the lead
                                            <strong> {lead.name}</strong> and all associated data.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={handleDeleteLead}
                                            disabled={deleting}
                                            className="bg-red-600 hover:bg-red-700"
                                        >
                                            {deleting ? 'Deleting...' : 'Delete'}
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        )}
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
                <Tabs defaultValue={defaultTab} className="h-full flex flex-col">
                    <div className="bg-white border-b border-slate-200 px-6">
                        <TabsList className="grid w-full max-w-2xl grid-cols-6">
                            <TabsTrigger value="about">About</TabsTrigger>
                            <TabsTrigger value="conversation">Conversation</TabsTrigger>
                            <TabsTrigger value="timeline">Timeline</TabsTrigger>
                            <TabsTrigger value="tasks">Tasks</TabsTrigger>
                            <TabsTrigger value="activity">Activity</TabsTrigger>
                            <TabsTrigger value="files">Files</TabsTrigger>
                        </TabsList>
                    </div>

                    <TabsContent value="about" className="flex-1 overflow-y-auto p-6 mt-0">
                        <div className="max-w-4xl mx-auto space-y-6">
                            <div className="bg-white rounded-lg border border-slate-200 p-6 relative group">
                                <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleEditClick}
                                        className="rounded-lg"
                                    >
                                        <Edit2 className="h-4 w-4 mr-2" />
                                        Edit
                                    </Button>
                                </div>

                                {/* Header with Name, Job Title, Company */}
                                <div className="mb-6 pb-6 border-b border-slate-200">
                                    <div className="flex items-start gap-4">
                                        {lead.logo_url && (
                                            <img
                                                src={lead.logo_url}
                                                alt={lead.company || 'Company logo'}
                                                className="w-16 h-16 rounded-lg object-contain border border-slate-200"
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                        )}
                                        {!lead.logo_url && lead.company && (
                                            <div className="w-16 h-16 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center">
                                                <Building2 className="h-8 w-8 text-slate-400" />
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <h2 className="text-2xl font-bold text-slate-900 mb-1">{lead.name}</h2>
                                            {lead.job_title && (
                                                <p className="text-lg text-slate-600 mb-1">{lead.job_title}</p>
                                            )}
                                            {lead.company && (
                                                <div className="flex items-center gap-2 text-slate-600">
                                                    <Building2 className="h-4 w-4" />
                                                    <span>{lead.company}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Quick Actions */}
                                <div className="flex flex-wrap gap-3 mb-6 gap-3">
                                    {lead.website && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => window.open(lead.website, '_blank')}
                                            className="rounded-lg"
                                        >
                                            <ExternalLink className="h-4 w-4 mr-2" />
                                            Website
                                        </Button>
                                    )}
                                    {lead.linkedin_profile && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => window.open(lead.linkedin_profile, '_blank')}
                                            className="rounded-lg"
                                        >
                                            <Linkedin className="h-4 w-4 mr-2" />
                                            LinkedIn
                                        </Button>
                                    )}
                                    {lead.phone_number && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => copyToClipboard(lead.phone_number)}
                                            className="rounded-lg"
                                        >
                                            {copySuccess ? (
                                                <Check className="h-4 w-4 mr-2" />
                                            ) : (
                                                <Phone className="h-4 w-4 mr-2" />
                                            )}
                                            {copySuccess ? 'Copied' : 'Copy Phone'}
                                        </Button>
                                    )}
                                </div>

                                {/* Field Display Grid */}
                                <div className="grid grid-cols-2 gap-6">
                                    <div>
                                        <Label className="text-slate-600 mb-1 block">Email</Label>
                                        <p className="text-sm text-slate-900">{lead.email}</p>
                                    </div>
                                    {lead.source && (
                                        <div>
                                            <Label className="text-slate-600 mb-1 block">Source</Label>
                                            <p className="text-sm text-slate-900">{lead.source}</p>
                                        </div>
                                    )}
                                    {lead.phone_number && (
                                        <div>
                                            <Label className="text-slate-600 mb-1 block">Phone Number</Label>
                                            <p className="text-sm text-slate-900">{lead.phone_number}</p>
                                        </div>
                                    )}
                                    {lead.estimated_value && (
                                        <div>
                                            <Label className="text-slate-600 mb-1 block">Estimated Value</Label>
                                            <p className="text-sm text-slate-900">{lead.estimated_value}</p>
                                        </div>
                                    )}
                                    <div>
                                        <Label className="text-slate-600 mb-1 block">Status</Label>
                                        <Select
                                            value={lead.status || ''}
                                            disabled={updatingStatus}
                                            onValueChange={async (stageId) => {
                                                setUpdatingStatus(true);
                                                try {
                                                    const response = await axios.patch(`${API_URL}/leads/${leadId}`, { status: stageId });
                                                    setLead(prev => ({ ...prev, status: response.data.status }));
                                                } catch (error) {
                                                    toast.error(getApiError(error, 'Failed to update status'));
                                                } finally {
                                                    setUpdatingStatus(false);
                                                }
                                            }}
                                        >
                                            <SelectTrigger className="text-sm h-8" disabled={updatingStatus}>
                                                <SelectValue placeholder="— No stage —" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {stages.map(s => (
                                                    <SelectItem key={s.id} value={s.id}>
                                                        <span className="flex items-center gap-2">
                                                            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: s.color }} />
                                                            {s.name}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {lead.sentiment_score && (
                                        <div>
                                            <Label className="text-slate-600 mb-1 block">Sentiment</Label>
                                            <span className={`text-xs px-2 py-1 rounded-md font-medium ${getSentimentColor(lead.sentiment_score)}`}>
                                                {lead.sentiment_score}
                                            </span>
                                        </div>
                                    )}
                                    <div>
                                        <Label className="text-slate-600 mb-1 block">Created</Label>
                                        <p className="text-sm text-slate-900">
                                            {format(new Date(lead.created_at), 'PPP')}
                                        </p>
                                    </div>
                                    <div>
                                        <Label className="text-slate-600 mb-1 block">Last Updated</Label>
                                        <p className="text-sm text-slate-900">
                                            {format(new Date(lead.updated_at), 'PPP')}
                                        </p>
                                    </div>
                                    {lead.initial_notes && (
                                        <div className="col-span-2">
                                            <Label className="text-slate-600 mb-1 block">Initial Notes</Label>
                                            <p className="text-sm text-slate-900 whitespace-pre-wrap">{lead.initial_notes}</p>
                                        </div>
                                    )}

                                    {/* Tags Section */}
                                    <div className="col-span-2">
                                        <div className="flex items-center justify-between mb-2">
                                            <Label className="text-slate-600">Tags</Label>
                                            {!editingTags && (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setEditingTags(true)}
                                                    className="h-6 px-2 text-xs"
                                                >
                                                    <Plus className="h-3 w-3 mr-1" />
                                                    Add
                                                </Button>
                                            )}
                                        </div>
                                        {editingTags ? (
                                            <div className="space-y-2">
                                                <TagSelect
                                                    value={lead.tags || []}
                                                    onChange={handleTagsChange}
                                                    placeholder="Select or create tags..."
                                                />
                                                <div className="flex gap-2">
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => setEditingTags(false)}
                                                        className="text-xs"
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-wrap gap-2">
                                                {lead.tags && lead.tags.length > 0 && tags.length > 0 ? (
                                                    tags
                                                        .filter(tag => tag && lead.tags.includes(tag.id))
                                                        .map((tag) => {
                                                            const bgColor = tag.color ? tag.color + '20' : '#e5e7eb';
                                                            const textColor = tag.color || '#374151';
                                                            return (
                                                                <Badge
                                                                    key={tag.id}
                                                                    variant="secondary"
                                                                    className="text-xs px-2 py-0.5 rounded-md font-medium"
                                                                    style={{
                                                                        backgroundColor: bgColor,
                                                                        color: textColor,
                                                                        borderColor: textColor + '40'
                                                                    }}
                                                                >
                                                                    {tag.name}
                                                                    <button
                                                                        onClick={() => handleTagsChange(lead.tags.filter(id => id !== tag.id))}
                                                                        className="ml-1.5 hover:bg-black/10 rounded-full p-0.5"
                                                                    >
                                                                        <X className="h-3 w-3" />
                                                                    </button>
                                                                </Badge>
                                                            );
                                                        })
                                                ) : (
                                                    <p className="text-sm text-slate-400">No tags assigned</p>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Owner Assignment Section */}
                                    <div className="col-span-2">
                                        <Label className="text-slate-600 mb-2 block">Assigned To</Label>
                                        {lead.owner_id ? (
                                            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                                                <Avatar className="h-8 w-8">
                                                    <AvatarFallback className="bg-slate-200">
                                                        {getInitials(getOwnerInfo(lead.owner_id)?.email || '')}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="flex-1">
                                                    <Select
                                                        value={lead.owner_id}
                                                        onValueChange={(value) => assignLead(value)}
                                                    >
                                                        <SelectTrigger className="w-full">
                                                            <SelectValue>
                                                                {getOwnerInfo(lead.owner_id)?.email || 'Unknown'}
                                                            </SelectValue>
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {teamMembers.map((member) => (
                                                                <SelectItem key={member.id} value={member.id}>
                                                                    <div className="flex items-center gap-2">
                                                                        <Avatar className="h-5 w-5">
                                                                            <AvatarFallback className="text-xs bg-slate-200">
                                                                                {getInitials(member.email)}
                                                                            </AvatarFallback>
                                                                        </Avatar>
                                                                        {member.email}
                                                                    </div>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                                                <User className="h-5 w-5 text-amber-600" />
                                                <div className="flex-1">
                                                    <p className="text-sm font-medium text-amber-900">Unassigned Lead</p>
                                                    <p className="text-xs text-amber-700">This lead hasn't been claimed yet</p>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    onClick={claimLead}
                                                    className="bg-amber-600 hover:bg-amber-700"
                                                >
                                                    <UserPlus className="h-4 w-4 mr-2" />
                                                    Claim Lead
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="conversation" className="flex-1 overflow-hidden mt-0 p-6 min-h-0">
                        <div className="h-full flex flex-col">
                            <ConversationView leadId={lead.id} leadEmail={lead.email} />
                        </div>
                    </TabsContent>

                    <TabsContent value="timeline" className="flex-1 overflow-hidden mt-0 p-6 min-h-0">
                        <div className="h-full flex flex-col">
                            <TimelineView leadId={lead.id} leadName={lead.name} />
                        </div>
                    </TabsContent>

                    <TabsContent value="tasks" className="flex-1 overflow-y-auto p-6 mt-0">
                        <div className="max-w-4xl mx-auto space-y-6">
                            {/* Task Creation Form */}
                            <div className="bg-white rounded-lg border border-slate-200 p-6">
                                <h2 className="text-xl font-semibold text-slate-900 mb-4">Create New Task</h2>
                                <form onSubmit={handleCreateTask} className="space-y-4">
                                    <div>
                                        <Label htmlFor="task-description">Description <span className="text-red-500">*</span></Label>
                                        <Input
                                            id="task-description"
                                            value={taskForm.description}
                                            onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                                            placeholder="Follow up on pricing"
                                            className="rounded-lg"
                                            required
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="task-type">Type</Label>
                                            <Select
                                                value={taskForm.type}
                                                onValueChange={(value) => setTaskForm({ ...taskForm, type: value })}
                                            >
                                                <SelectTrigger className="rounded-lg">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="Call">Call</SelectItem>
                                                    <SelectItem value="WhatsApp">WhatsApp</SelectItem>
                                                    <SelectItem value="Email">Email</SelectItem>
                                                    <SelectItem value="SMS">SMS</SelectItem>
                                                    <SelectItem value="Meeting">Meeting</SelectItem>
                                                    <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                                                    <SelectItem value="Todo">Todo</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div>
                                            <Label htmlFor="task-due-date">Due Date <span className="text-red-500">*</span></Label>
                                            <Input
                                                id="task-due-date"
                                                type="date"
                                                value={taskForm.due_date}
                                                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                                                className="rounded-lg"
                                                required
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label htmlFor="task-assignee">Assignee</Label>
                                        <Select
                                            value={taskForm.assignee_id}
                                            onValueChange={(value) => setTaskForm({ ...taskForm, assignee_id: value })}
                                        >
                                            <SelectTrigger className="rounded-lg">
                                                <SelectValue placeholder="Select assignee" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {teamMembers.map((member) => (
                                                    <SelectItem key={member.id} value={member.id}>
                                                        <div className="flex items-center gap-2">
                                                            <Avatar className="h-5 w-5">
                                                                <AvatarFallback className="text-xs bg-slate-200">
                                                                    {getInitials(member.email)}
                                                                </AvatarFallback>
                                                            </Avatar>
                                                            {member.email}
                                                        </div>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <Button
                                        type="submit"
                                        disabled={creatingTask}
                                        className="w-full rounded-lg"
                                    >
                                        {creatingTask ? 'Creating...' : 'Create Task'}
                                    </Button>
                                </form>
                            </div>

                            {/* Pending Tasks List */}
                            <div className="bg-white rounded-lg border border-slate-200 p-6">
                                <h2 className="text-xl font-semibold text-slate-900 mb-4">Pending Tasks</h2>
                                {tasks.length === 0 ? (
                                    <p className="text-slate-500 text-center py-8">No pending tasks for this lead</p>
                                ) : (
                                    <div className="space-y-3">
                                        {tasks.map((task) => {
                                            const isOverdue = new Date(task.due_date) < new Date() && new Date(task.due_date).toDateString() !== new Date().toDateString();
                                            const isToday = new Date(task.due_date).toDateString() === new Date().toDateString();
                                            const assignee = teamMembers.find(m => m.id === task.assignee_id);
                                            const dueMid = new Date(task.due_date).setHours(0, 0, 0, 0);
                                            const todayMid = new Date().setHours(0, 0, 0, 0);
                                            const daysOverdue = Math.floor((todayMid - dueMid) / (1000 * 60 * 60 * 24));

                                            return (
                                                <div
                                                    key={task.id}
                                                    className={`p-4 rounded-lg border ${isOverdue
                                                        ? 'bg-red-50 border-red-200'
                                                        : isToday
                                                            ? 'bg-green-50 border-green-200'
                                                            : 'bg-slate-50 border-slate-200'
                                                        }`}
                                                >
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex items-start gap-3 flex-1">
                                                            <div className={`mt-1 ${isOverdue ? 'text-red-600' : isToday ? 'text-green-600' : 'text-slate-600'}`}>
                                                                {getTaskTypeIcon(task.type)}
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-sm font-medium text-slate-900 mb-1">
                                                                    {task.description}
                                                                </p>
                                                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                                                    <span className="flex items-center gap-1">
                                                                        <Calendar className="h-3 w-3" />
                                                                        {format(new Date(task.due_date), 'MMM d, yyyy')}
                                                                    </span>
                                                                    {assignee && (
                                                                        <span className="flex items-center gap-1">
                                                                            <User className="h-3 w-3" />
                                                                            {assignee.email}
                                                                        </span>
                                                                    )}
                                                                    {isOverdue && (
                                                                        <span className="text-red-600 font-medium">
                                                                            {daysOverdue} Days Overdue
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 ml-4">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleCompleteTask(task.id)}
                                                                className="rounded-lg"
                                                            >
                                                                <CheckSquare className="h-4 w-4" />
                                                            </Button>
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDeleteTask(task.id)}
                                                                className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                                                            >
                                                                <X className="h-4 w-4" />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Active Cadences */}
                            {cadenceEnrollments.filter(e => e.status === 'active').length > 0 && (
                                <div className="bg-white rounded-lg border border-slate-200 p-6">
                                    <h2 className="text-xl font-semibold text-slate-900 mb-4 flex items-center gap-2">
                                        <Repeat className="h-5 w-5 text-violet-500" />
                                        Active Cadences
                                    </h2>
                                    <div className="space-y-4">
                                        {cadenceEnrollments.filter(e => e.status === 'active').map(enrollment => {
                                            const completedCount = enrollment.steps.filter(s => s.status === 'completed').length;
                                            const totalCount = enrollment.steps.length;
                                            return (
                                                <div key={enrollment.id} className="border border-violet-200 bg-violet-50 rounded-lg p-4">
                                                    <div className="flex items-start justify-between mb-3">
                                                        <div>
                                                            <p className="font-semibold text-slate-900">{enrollment.cadence_name}</p>
                                                            <p className="text-xs text-slate-500 mt-0.5">
                                                                Started {format(new Date(enrollment.start_date), 'MMM d, yyyy')} · {completedCount}/{totalCount} steps done
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => handleCancelEnrollment(enrollment.id)}
                                                            className="text-red-400 hover:text-red-600 transition-colors"
                                                            title="Cancel cadence"
                                                        >
                                                            <XCircle className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                    <div className="flex items-center gap-1 flex-wrap">
                                                        {enrollment.steps.map((step, idx) => (
                                                            <React.Fragment key={step.step_number}>
                                                                <div className={`flex flex-col items-center text-center min-w-[60px] ${step.status === 'completed' ? 'text-green-700' : step.status === 'skipped' ? 'text-slate-400 line-through' : 'text-slate-600'}`}>
                                                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 ${step.status === 'completed' ? 'bg-green-100 border-green-400 text-green-700' : step.status === 'skipped' ? 'bg-slate-100 border-slate-300 text-slate-400' : 'bg-white border-violet-300 text-violet-600'}`}>
                                                                        {step.status === 'completed' ? '✓' : step.step_number}
                                                                    </div>
                                                                    <span className="text-[10px] mt-1 leading-tight">
                                                                        {(() => { const d = step.scheduled_date ? new Date(step.scheduled_date) : null; return d && !isNaN(d.getTime()) ? format(d, 'MMM d') : '-'; })()}
                                                                    </span>
                                                                </div>
                                                                {idx < enrollment.steps.length - 1 && (
                                                                    <ChevronRight className="h-3 w-3 text-slate-300 flex-shrink-0" />
                                                                )}
                                                            </React.Fragment>
                                                        ))}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="activity" className="flex-1 overflow-hidden p-6 mt-0">
                        <ActivityCalendar leadId={leadId} leadName={lead?.name} />
                    </TabsContent>

                    <TabsContent value="files" className="flex-1 overflow-y-auto p-6 mt-0">
                        <div className="max-w-3xl mx-auto space-y-6">
                            <div className="bg-white rounded-lg border border-slate-200 p-6">
                                <h3 className="text-sm font-semibold text-slate-700 mb-4">Upload Files</h3>
                                <FileUpload
                                    leadId={lead?.id}
                                    entityType="general"
                                    uploadAfterCreate={false}
                                    onUploaded={() => setFilesRefreshKey(k => k + 1)}
                                />
                            </div>
                            <div className="bg-white rounded-lg border border-slate-200 p-6">
                                <h3 className="text-sm font-semibold text-slate-700 mb-4">All Attachments</h3>
                                <FileGallery leadId={lead?.id} refreshKey={filesRefreshKey} />
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Edit Lead Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="rounded-2xl max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-semibold text-slate-900">Edit Lead</DialogTitle>
                    </DialogHeader>
                    {editingLead && (
                        <div className="space-y-6 mt-4">
                            {/* Essential Info Section */}
                            <div className="space-y-4">
                                <div className="border-b border-slate-200 pb-2">
                                    <h3 className="text-lg font-semibold text-slate-900">Essential Info</h3>
                                </div>

                                <div>
                                    <Label htmlFor="edit-name">Full Name <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="edit-name"
                                        value={editingLead.name}
                                        onChange={(e) => setEditingLead({ ...editingLead, name: e.target.value })}
                                        placeholder="John Doe"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-email">Email <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="edit-email"
                                        type="email"
                                        value={editingLead.email}
                                        onChange={(e) => setEditingLead({ ...editingLead, email: e.target.value })}
                                        placeholder="john@example.com"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-company">Company Name <span className="text-red-500">*</span></Label>
                                    <Input
                                        id="edit-company"
                                        value={editingLead.company}
                                        onChange={(e) => setEditingLead({ ...editingLead, company: e.target.value })}
                                        placeholder="Acme Corp"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-source">Source <span className="text-red-500">*</span></Label>
                                    <Select
                                        value={editingLead.source}
                                        onValueChange={(value) => setEditingLead({ ...editingLead, source: value })}
                                    >
                                        <SelectTrigger className="rounded-lg">
                                            <SelectValue placeholder="Select source" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="Cold Outbound">Cold Outbound</SelectItem>
                                            <SelectItem value="Email Campaign">Email Campaign</SelectItem>
                                            <SelectItem value="Website">Website</SelectItem>
                                            <SelectItem value="Referral">Referral</SelectItem>
                                            <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                                            <SelectItem value="Other">Other</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Enrichment Section */}
                            <div className="space-y-4">
                                <div className="border-b border-slate-200 pb-2">
                                    <h3 className="text-lg font-semibold text-slate-900">Enrichment</h3>
                                </div>

                                <div>
                                    <Label htmlFor="edit-job_title">Job Title</Label>
                                    <Input
                                        id="edit-job_title"
                                        value={editingLead.job_title}
                                        onChange={(e) => setEditingLead({ ...editingLead, job_title: e.target.value })}
                                        placeholder="CTO"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-website">Website</Label>
                                    <Input
                                        id="edit-website"
                                        type="url"
                                        value={editingLead.website}
                                        onChange={(e) => setEditingLead({ ...editingLead, website: e.target.value })}
                                        placeholder="https://example.com"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-phone_number">Phone Number</Label>
                                    <Input
                                        id="edit-phone_number"
                                        type="tel"
                                        value={editingLead.phone_number}
                                        onChange={(e) => setEditingLead({ ...editingLead, phone_number: e.target.value })}
                                        placeholder="+1 (555) 123-4567"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-linkedin_profile">LinkedIn Profile</Label>
                                    <Input
                                        id="edit-linkedin_profile"
                                        type="url"
                                        value={editingLead.linkedin_profile}
                                        onChange={(e) => setEditingLead({ ...editingLead, linkedin_profile: e.target.value })}
                                        placeholder="https://linkedin.com/in/johndoe"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-estimated_value">Estimated Value</Label>
                                    <Input
                                        id="edit-estimated_value"
                                        value={editingLead.estimated_value}
                                        onChange={(e) => setEditingLead({ ...editingLead, estimated_value: e.target.value })}
                                        placeholder="$10,000"
                                        className="rounded-lg"
                                    />
                                </div>

                                <div>
                                    <Label htmlFor="edit-initial_notes">Initial Notes</Label>
                                    <Textarea
                                        id="edit-initial_notes"
                                        value={editingLead.initial_notes}
                                        onChange={(e) => setEditingLead({ ...editingLead, initial_notes: e.target.value })}
                                        placeholder="Add any initial notes about this lead..."
                                        className="rounded-lg min-h-[100px]"
                                    />
                                </div>

                                <div className="flex items-center justify-between rounded-lg border p-4">
                                    <div>
                                        <Label htmlFor="edit-do_not_contact">Do Not Contact</Label>
                                        <p className="text-sm text-slate-500">Exclude from campaigns and future outreach</p>
                                    </div>
                                    <Switch
                                        id="edit-do_not_contact"
                                        checked={editingLead.do_not_contact || false}
                                        onCheckedChange={(checked) => setEditingLead({ ...editingLead, do_not_contact: checked })}
                                    />
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4">
                                <Button
                                    variant="outline"
                                    onClick={() => setEditDialogOpen(false)}
                                    className="flex-1 rounded-lg"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateLead}
                                    className="flex-1 rounded-lg font-medium transition-all active:scale-95 bg-slate-900 hover:bg-slate-800 text-white"
                                >
                                    Save Changes
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ScheduleFollowUpModal
                open={followUpModalOpen}
                onClose={() => setFollowUpModalOpen(false)}
                leadId={leadId}
                leadName={lead?.name}
                onSaved={fetchTasks}
            />

            {completingTask && (
                <LogInteractionModal
                    open={logInteractionOpen}
                    onClose={handleLogInteractionClose}
                    leadId={leadId}
                    leadName={lead?.name}
                    onSaved={handleInteractionSaved}
                />
            )}
        </div>
    );
};

export default LeadDetailPage;

